import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Radio,
  useTheme
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AccountBalance as BankIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WalletService from '../../services/wallet.service';
import { getBrandTint } from '../../utils/colorUtils';

const FS = '0.8125rem';
const SECTION_TITLE_SX = { fontSize: '1rem', fontWeight: 600, mb: 0.5 };
const FIELD_SX = { '& .MuiInputBase-root': { fontSize: FS }, '& .MuiInputLabel-root': { fontSize: FS } };

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

const PortalTopUp = ({ portalUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const fetchBankAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const accounts = await WalletService.getPortalBankAccounts();
      setBankAccounts(accounts);
      const defaultBank = accounts.find(a => a.isDefault);
      if (defaultBank) setSelectedBank(defaultBank.id);
    } catch (err) {
      console.error('Failed to fetch bank accounts:', err);
      setError('Failed to load bank accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts]);

  // Cleanup file preview URL on unmount
  useEffect(() => {
    return () => {
      if (filePreview && filePreview.startsWith('blob:')) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_SIZE) {
      setError('File size exceeds 3MB limit.');
      return;
    }

    const isPdf = selectedFile.type === 'application/pdf';
    const isImage = selectedFile.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setError('Invalid file type. Please upload a bank receipt image or PDF.');
      return;
    }

    setFile(selectedFile);
    setError(null);

    if (isImage) {
      setFilePreview(URL.createObjectURL(selectedFile));
    } else {
      setFilePreview(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (!droppedFile) return;

    if (droppedFile.size > MAX_FILE_SIZE) {
      setError('File size exceeds 3MB limit.');
      return;
    }

    const isPdf = droppedFile.type === 'application/pdf';
    const isImage = droppedFile.type.startsWith('image/');
    if (!isPdf && !isImage) {
      setError('Invalid file type. Please upload a bank receipt image or PDF.');
      return;
    }

    setFile(droppedFile);
    setError(null);

    if (isImage) {
      setFilePreview(URL.createObjectURL(droppedFile));
    } else {
      setFilePreview(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleSubmit = async () => {
    if (!selectedBank || !file || !amount) return;

    setSubmitting(true);
    setError(null);

    try {
      await WalletService.submitPortalTopup({
        tcBankAccountId: selectedBank,
        amount: parseFloat(amount),
        remarks: remarks || undefined,
        bankReference: bankReference.trim() || undefined
      }, file);
      setSubmitSuccess(true);
      setTimeout(() => {
        navigate('/portal/wallet');
      }, 1500);
    } catch (err) {
      console.error('Failed to submit top-up request:', err);
      const apiMsg = err?.response?.data?.error || err?.response?.data?.message;
      setError(apiMsg || 'Failed to submit top-up request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (submitSuccess) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: 1.5 }}>
        <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main' }} />
        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>Top-Up Request Submitted</Typography>
        <Typography sx={{ fontSize: FS, color: 'text.secondary' }}>Your request is being reviewed by the typing center.</Typography>
        <CircularProgress size={20} sx={{ mt: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      <Box sx={{ mb: 1.5 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/portal/wallet')} sx={{ textTransform: 'none', fontSize: FS }}>
          Back to Wallet
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step 1: Select Bank Account */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography sx={SECTION_TITLE_SX}>1. Select Bank Account to Deposit</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
          Choose the bank account where you made the deposit.
        </Typography>

        {bankAccounts.length === 0 ? (
          <Alert severity="info">No active bank accounts available. Please contact the typing center.</Alert>
        ) : (
          <Grid container spacing={1.5}>
            {bankAccounts.map((bank) => {
              const selected = selectedBank === bank.id;
              return (
                <Grid item xs={12} sm={6} md={4} key={bank.id}>
                  <Box
                    onClick={() => setSelectedBank(bank.id)}
                    sx={{
                      p: 1.5,
                      height: '100%',
                      cursor: 'pointer',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? getBrandTint(theme, 0.06) : 'background.paper',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: 'primary.main' }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Radio checked={selected} size="small" sx={{ p: 0 }} />
                      <BankIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography sx={{ fontSize: FS, fontWeight: 600 }}>{bank.bankName}</Typography>
                      {bank.isDefault && (
                        <Typography sx={{ fontSize: '0.6875rem', color: 'warning.main', fontWeight: 600 }}>Default</Typography>
                      )}
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{bank.accountName}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontFamily: 'monospace', mt: 0.25 }}>
                      Acc: {bank.accountNumber}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', fontFamily: 'monospace' }}>
                      IBAN: {bank.iban}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>

      {/* Step 2: Upload Receipt */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ ...SECTION_TITLE_SX, mb: 1 }}>2. Upload Bank Receipt</Typography>
        <Alert severity="info" sx={{ mb: 1.5, fontSize: FS, py: 0.25 }}>
          Paying by cheque? Hand the cheque to the typing center instead — it is added to your wallet once it clears.
          Upload here only a bank transfer or deposit receipt.
        </Alert>

        {/* Drop zone */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: file ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: 'action.hover',
            transition: 'all 0.15s',
            '&:hover': { borderColor: 'primary.main', bgcolor: getBrandTint(theme, 0.04) }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          {!file ? (
            <>
              <UploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.5 }} />
              <Typography sx={{ fontSize: FS, fontWeight: 500, mb: 0.25 }}>
                Drag & drop your receipt here, or click to browse
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                Accepts images (JPG, PNG) and PDF files. Max 3MB.
              </Typography>
            </>
          ) : (
            <Box>
              {filePreview ? (
                <Box sx={{ mb: 1 }}>
                  <img
                    src={filePreview}
                    alt="Receipt preview"
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: 8, objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <PdfIcon sx={{ fontSize: 40, color: 'error.main', mb: 0.5 }} />
              )}
              <Typography sx={{ fontSize: FS, fontWeight: 500 }}>{file.name}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Button size="small" onClick={(e) => { e.stopPropagation(); setFile(null); setFilePreview(null); }} sx={{ textTransform: 'none', fontSize: FS }}>
                  Change File
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Step 3: Amount & Remarks */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography sx={{ ...SECTION_TITLE_SX, mb: 1.5 }}>3. Enter Amount</Typography>
        <Grid container spacing={1.5} sx={FIELD_SX}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Amount (AED)"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              inputProps={{ min: 0, step: '0.01' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Bank reference (optional)"
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
              inputProps={{ maxLength: 100 }}
              placeholder="The reference of your bank transfer"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Remarks (optional)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="e.g., Bank transfer on 21st April"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Submit */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button onClick={() => navigate('/portal/wallet')} sx={{ textTransform: 'none', fontSize: FS }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !selectedBank || !file || !amount || parseFloat(amount) <= 0}
          sx={{ textTransform: 'none', fontSize: FS, minWidth: 160 }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit Top-Up Request'}
        </Button>
      </Box>
    </Box>
  );
};

export default PortalTopUp;
